import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ZAxis,
} from 'recharts';
import api from '../../../services/api';

const CHART_LIMIT = 10000;
const COLOURS = {
  primary: '#072040',
  secondary: '#f0a500',
  info: '#599BE3',
  success: '#91CF9B',
  danger: '#ffc107',
  gray: '#6c757d',
};

const NUMERIC_HINTS = ['INT', 'NUMERIC', 'DECIMAL', 'FLOAT', 'DOUBLE', 'REAL', 'MONEY'];
const DATE_HINTS = ['DATE', 'TIME', 'TIMESTAMP'];

const isNumericType = (t) => t && NUMERIC_HINTS.some((h) => t.toUpperCase().includes(h));
const isDateType = (t) => t && DATE_HINTS.some((h) => t.toUpperCase().includes(h));

const coerceNumber = (v) => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const ChartPanel = ({ schema, table, columns, columnTypes, filters, sort, globalFilter }) => {
  const [chartType, setChartType] = useState('line');
  const [pickedX, setPickedX] = useState(null);
  const [pickedY, setPickedY] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // derive defaults from columns; user picks override
  const defaultX = useMemo(() => {
    if (!columns || columns.length === 0) return null;
    const dateCol = columns.find((c) => isDateType(columnTypes?.[c]?.type));
    const numCol = columns.find((c) => isNumericType(columnTypes?.[c]?.type));
    return dateCol || numCol || columns[0];
  }, [columns, columnTypes]);

  const xCol = pickedX && columns.includes(pickedX) ? pickedX : defaultX;

  const defaultY = useMemo(() => {
    if (!columns || columns.length === 0) return null;
    const candidates = columns.filter(
      (c) => isNumericType(columnTypes?.[c]?.type) && c !== xCol,
    );
    return candidates[0] || columns.find((c) => c !== xCol) || null;
  }, [columns, columnTypes, xCol]);

  const yCol = pickedY && columns.includes(pickedY) && pickedY !== xCol ? pickedY : defaultY;

  // fetch chart data when inputs change
  useEffect(() => {
    if (!schema || !table || !xCol || !yCol) return undefined;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.post(
          `/api/db-explorer/tables/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/rows`,
          {
            filters,
            sort_column: sort?.column || xCol,
            sort_direction: sort?.direction || 'asc',
            global_filter: globalFilter || null,
            limit: CHART_LIMIT,
            offset: 0,
            include_count: false,
          },
        );
        if (cancelled) return;
        const cols = res.data.columns || [];
        const xIdx = cols.indexOf(xCol);
        const yIdx = cols.indexOf(yCol);
        if (xIdx < 0 || yIdx < 0) {
          setRows([]);
          return;
        }
        const xIsNumeric = isNumericType(columnTypes?.[xCol]?.type);
        const data = (res.data.rows || [])
          .map((r) => {
            const xv = r[xIdx];
            const yv = coerceNumber(r[yIdx]);
            if (yv == null) return null;
            const xOut = xIsNumeric ? coerceNumber(xv) : xv == null ? null : String(xv);
            if (xOut == null) return null;
            return { x: xOut, y: yv };
          })
          .filter(Boolean);
        setRows(data);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [schema, table, xCol, yCol, filters, sort, globalFilter, columnTypes]);

  const xIsNumeric = useMemo(() => isNumericType(columnTypes?.[xCol]?.type), [columnTypes, xCol]);

  const renderChart = () => {
    if (rows.length === 0) {
      return <div className="db-explorer-empty">No chartable data for the current selection.</div>;
    }
    const common = {
      data: rows,
      margin: { top: 12, right: 24, left: 12, bottom: 24 },
    };

    if (chartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart {...common}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="x" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="y" name={yCol} fill={COLOURS.primary} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'scatter') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart {...common}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="x" type={xIsNumeric ? 'number' : 'category'} name={xCol} tick={{ fontSize: 11 }} />
            <YAxis dataKey="y" name={yCol} tick={{ fontSize: 11 }} />
            <ZAxis range={[40, 40]} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Legend />
            <Scatter name={`${xCol} vs ${yCol}`} data={rows} fill={COLOURS.secondary} />
          </ScatterChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart {...common}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="x" type={xIsNumeric ? 'number' : 'category'} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="y" name={yCol} stroke={COLOURS.primary} dot={false} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="db-explorer-chart">
      <div className="db-explorer-chart-controls">
        <label>
          <span>Type</span>
          <select className="form-select form-select-sm" value={chartType} onChange={(e) => setChartType(e.target.value)}>
            <option value="line">Line</option>
            <option value="bar">Bar</option>
            <option value="scatter">Scatter</option>
          </select>
        </label>
        <label>
          <span>X</span>
          <select className="form-select form-select-sm" value={xCol || ''} onChange={(e) => setPickedX(e.target.value)}>
            {columns.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          <span>Y</span>
          <select className="form-select form-select-sm" value={yCol || ''} onChange={(e) => setPickedY(e.target.value)}>
            {columns.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <span className="db-explorer-chart-status">
          {loading ? 'Loading…' : `${rows.length.toLocaleString()} points (max ${CHART_LIMIT.toLocaleString()})`}
        </span>
      </div>

      {error && <div className="alert alert-warning db-explorer-alert">{error}</div>}

      <div className="db-explorer-chart-area">
        {renderChart()}
      </div>
    </div>
  );
};

export default ChartPanel;
